import { useState } from 'react';
import { Tabs, TabsContent } from '../../components/ui/tabs';
import { Building, Bell, Shield } from 'lucide-react';

import toast from 'react-hot-toast';
import { authAPI } from '../../services/api';
import {
  VALIDATION_MESSAGES,
  REGEX_PATTERNS,
} from '../../utils/validationMessages';
import PageHeader from '../../components/common/PageHeader';
import LoaderOverlay from '../../components/common/LoaderOverlay';
import SettingsTabs, {
  TabConfig,
} from '../../components/settings/SettingsTabs';
import ChangePassword from '../../components/settings/ChangePassword';
import SecurityStatus from '../../components/settings/SecurityStatus';
import TwoFactorAuth from '../../components/settings/TwoFactorAuth';
import CompanyInformation from '../../components/settings/CompanyInformation';
import NotificationTypes from '../../components/settings/NotificationTypes';
import NotificationChannels from '../../components/settings/NotificationChannels';

interface SettingsState {
  company: {
    name: string;
    email: string;
    phone: string;
    address: string;
    city: string;
    country: string;
    website: string;
    logo: string;
  };
  notifications: {
    email: boolean;
    sms: boolean;
    push: boolean;
    marketing: boolean;
    security: boolean;
    updates: boolean;
  };
  security: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
    twoFactor: boolean;
    sessionTimeout: string;
  };
}

interface PasswordFormErrors {
  currentPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
}

const Settings = () => {
  const [activeTab, setActiveTab] = useState('company');
  const [isSaving, setIsSaving] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState<PasswordFormErrors>({});

  const tabs: TabConfig[] = [
    {
      value: 'company',
      label: 'Company',
      icon: <Building className="h-4 w-4" />,
    },
    {
      value: 'notifications',
      label: 'Notifications',
      shortLabel: 'Notify',
      icon: <Bell className="h-4 w-4" />,
    },
    {
      value: 'security',
      label: 'Security',
      icon: <Shield className="h-4 w-4" />,
    },
  ];

  const [settings, setSettings] = useState<SettingsState>({
    company: {
      name: 'Forex CRM Solutions',
      email: 'info@forexcrm.com',
      phone: '+1 234-567-8900',
      address: '123 Business Street',
      city: 'New York',
      country: 'United States',
      website: 'https://forexcrm.com',
      logo: '',
    },
    notifications: {
      email: true,
      sms: false,
      push: true,
      marketing: false,
      security: true,
      updates: true,
    },
    security: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
      twoFactor: false,
      sessionTimeout: '30',
    },
  });

  const handleInputChange = (
    section: keyof SettingsState,
    field: string,
    value: string | boolean
  ) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const isPasswordFormValid = () => {
    const { currentPassword, newPassword, confirmPassword } = settings.security;
    return (
      currentPassword.trim() !== '' &&
      newPassword.trim() !== '' &&
      confirmPassword.trim() !== '' &&
      !passwordErrors.currentPassword &&
      !passwordErrors.newPassword &&
      !passwordErrors.confirmPassword &&
      newPassword.length >= 8 &&
      newPassword.length <= 50 &&
      REGEX_PATTERNS.password.strong.test(newPassword) &&
      newPassword === confirmPassword
    );
  };

  const validatePasswordField = (field: string, value: string) => {
    const newErrors: PasswordFormErrors = { ...passwordErrors };

    if (field === 'currentPassword') {
      if (!value.trim()) {
        newErrors.currentPassword = 'Current password is required';
      } else {
        newErrors.currentPassword = undefined;
      }
    }

    if (field === 'newPassword') {
      if (!value.trim()) {
        newErrors.newPassword = VALIDATION_MESSAGES.password.required;
      } else if (value.length < 8) {
        newErrors.newPassword = VALIDATION_MESSAGES.password.minLength;
      } else if (value.length > 50) {
        newErrors.newPassword = VALIDATION_MESSAGES.password.maxLength;
      } else if (!REGEX_PATTERNS.password.strong.test(value)) {
        newErrors.newPassword = VALIDATION_MESSAGES.password.weak;
      } else {
        newErrors.newPassword = undefined;
      }

      if (settings.security.confirmPassword) {
        if (value !== settings.security.confirmPassword) {
          newErrors.confirmPassword =
            VALIDATION_MESSAGES.confirmPassword.mismatch;
        } else {
          newErrors.confirmPassword = undefined;
        }
      }
    }

    if (field === 'confirmPassword') {
      if (!value.trim()) {
        newErrors.confirmPassword =
          VALIDATION_MESSAGES.confirmPassword.required;
      } else if (settings.security.newPassword !== value) {
        newErrors.confirmPassword =
          VALIDATION_MESSAGES.confirmPassword.mismatch;
      } else {
        newErrors.confirmPassword = undefined;
      }
    }

    setPasswordErrors(newErrors);
  };

  const handlePasswordInputChange = (field: string, value: string) => {
    handleInputChange('security', field, value);
    validatePasswordField(field, value);
  };

  const handleSave = async () => {
    setIsSaving(true);

    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      toast.success('Settings saved successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    const { currentPassword, newPassword, confirmPassword } = settings.security;

    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Please fill in all password fields');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('New password must be at least 8 characters long');
      return;
    }
    setIsSaving(true);

    try {
      const response = await authAPI.changePassword({
        currentPassword,
        newPassword,
      });
      if (response.success || response.status === true) {
        toast.success(response.message || 'Password changed successfully');
        setSettings(prev => ({
          ...prev,
          security: {
            ...prev.security,
            currentPassword: '',
            newPassword: '',
            confirmPassword: '',
          },
        }));
      } else {
        toast.error(response.message || 'Failed to change password');
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to change password';
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6 px-4">
      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="space-y-6"
      >
        <div className="sticky top-0 z-20 bg-background pt-4">
          <PageHeader
            title="Settings"
            description=" Manage your account settings and preferences"
          />

          <div className="mt-4">
            <SettingsTabs tabs={tabs} />
          </div>
        </div>

        <TabsContent value="company">
          <CompanyInformation
            company={settings.company}
            isSaving={isSaving}
            onInputChange={(field, value) =>
              handleInputChange('company', field, value)
            }
            onSave={handleSave}
          />
        </TabsContent>

        <TabsContent value="notifications">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <NotificationChannels
              title="Notification Preferences"
              items={[
                {
                  key: 'email',
                  label: 'Email Notifications',
                  description: 'Receive notifications via email',
                  enabled: settings.notifications.email,
                },
                {
                  key: 'sms',
                  label: 'SMS Notifications',
                  description: 'Receive notifications via SMS',
                  enabled: settings.notifications.sms,
                },
                {
                  key: 'push',
                  label: 'Push Notifications',
                  description: 'Receive push notifications',
                  enabled: settings.notifications.push,
                },
              ]}
              onToggle={key =>
                handleInputChange(
                  'notifications',
                  key,
                  !settings.notifications[
                    key as keyof typeof settings.notifications
                  ]
                )
              }
            />

            <NotificationTypes
              items={[
                {
                  key: 'marketing',
                  label: 'Marketing',
                  description: 'Marketing and promotional emails',
                  enabled: settings.notifications.marketing,
                },
                {
                  key: 'security',
                  label: 'Security Alerts',
                  description: 'Security and login notifications',
                  enabled: settings.notifications.security,
                },
                {
                  key: 'updates',
                  label: 'Product Updates',
                  description: 'New features and updates',
                  enabled: settings.notifications.updates,
                },
              ]}
              onToggle={key =>
                handleInputChange(
                  'notifications',
                  key,
                  !settings.notifications[
                    key as keyof typeof settings.notifications
                  ]
                )
              }
              onSave={handleSave}
              isSaving={isSaving}
            />
          </div>
        </TabsContent>

        <TabsContent value="security">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <LoaderOverlay
                isLoading={isSaving}
                loadingText="Updating password..."
              >
                <ChangePassword
                  currentPassword={settings.security.currentPassword}
                  newPassword={settings.security.newPassword}
                  confirmPassword={settings.security.confirmPassword}
                  passwordErrors={passwordErrors}
                  isSaving={isSaving}
                  onCurrentPasswordChange={value =>
                    handlePasswordInputChange('currentPassword', value)
                  }
                  onNewPasswordChange={value =>
                    handlePasswordInputChange('newPassword', value)
                  }
                  onConfirmPasswordChange={value =>
                    handlePasswordInputChange('confirmPassword', value)
                  }
                  onSubmit={handlePasswordChange}
                  isPasswordFormValid={isPasswordFormValid}
                  description="Update your password to keep your account secure"
                />
              </LoaderOverlay>

              <TwoFactorAuth
                twoFactorEnabled={settings.security.twoFactor}
                onToggle={() =>
                  handleInputChange(
                    'security',
                    'twoFactor',
                    !settings.security.twoFactor
                  )
                }
              />
            </div>

            <div>
              <SecurityStatus twoFactorEnabled={settings.security.twoFactor} />
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Settings;
